import type { ConsensusResult } from "../lib/types";

interface ConsensusBarProps {
	consensus: ConsensusResult;
	// thresholdPct is server-derived from the proposer's role (PM=0, Trader=30,
	// RM=40, Analyst=50). The bar renders a thin marker at that point so a
	// glance shows how far the "yes" share is from clearing.
	thresholdPct: number;
	// Compact toggles a denser layout used in the Fam viewer's open-ideas list.
	compact?: boolean;
}

// ConsensusBar mirrors the visual rhythm of the rendered OG IdeaCard
// (landing/src/components/og/IdeaCard.tsx#ConsensusBar) so a Fam member
// scrolling between a shared X card and the in-app vote screen sees the same
// stacked yes/no bar. All math is server-side: percentages are derived from
// the supplied counts; we never reason about pass/fail locally.
export function ConsensusBar({
	consensus,
	thresholdPct,
	compact = false,
}: ConsensusBarProps) {
	const total = Math.max(consensus.yes + consensus.no, 1);
	const yesPct = (consensus.yes / total) * 100;
	const noPct = (consensus.no / total) * 100;

	// thresholdPct is fractional on the wire (e.g. 0.30 for Trader). Coerce to
	// 0–100 for the marker offset.
	const thresholdAt = thresholdPct <= 1 ? thresholdPct * 100 : thresholdPct;

	const barHeight = compact ? "h-2" : "h-3";

	return (
		<div className="space-y-2">
			<div
				className={`relative w-full overflow-hidden rounded-full bg-white/5 ${barHeight}`}
			>
				<div className="flex h-full w-full">
					<div
						className="h-full bg-emerald-500"
						style={{ width: `${yesPct}%` }}
					/>
					<div
						className="h-full bg-[var(--color-danger)]"
						style={{ width: `${noPct}%` }}
					/>
				</div>
				{/* Threshold marker — only render when below 100 to avoid sitting
				    right on the edge. */}
				{thresholdAt > 0 && thresholdAt < 100 && (
					<div
						className="pointer-events-none absolute top-0 h-full w-px bg-white/70"
						style={{ left: `${thresholdAt}%` }}
						aria-label={`Pass threshold ${thresholdAt.toFixed(0)}%`}
					/>
				)}
			</div>
			{!compact && (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
					<span className="text-emerald-300">Yes {consensus.yes}</span>
					<span className="text-[var(--color-danger)]">No {consensus.no}</span>
					<span className="ml-auto text-[var(--color-muted)]">
						Threshold {thresholdAt.toFixed(0)}% · Quorum{" "}
						{(consensus.quorumPct * 100).toFixed(0)}% of{" "}
						{consensus.membersTotal}
					</span>
				</div>
			)}
			{compact && (
				<div className="flex items-center gap-3 text-[11px] text-[var(--color-muted)]">
					<span>
						<span className="text-emerald-300">Yes</span> {consensus.yes}
					</span>
					<span>
						<span className="text-[var(--color-danger)]">No</span>{" "}
						{consensus.no}
					</span>
					<span className="ml-auto">Threshold {thresholdAt.toFixed(0)}%</span>
				</div>
			)}
		</div>
	);
}
